// Live-updates store — the client half of the fsgnext supergraph's change
// feed (the `liveChanges` GraphQL subscription, delivered over graphql-ws).
// See docs/live-updates.md for the full contract this file implements.
//
// The server pushes only WHAT changed (service/kind/scope/ids), never the
// changed data, so nothing here trusts pushed content: a notice bumps a
// per-kind revision counter, and the queries that care re-read through the
// normal auth-gated GraphQL path (src/composables/useLive.ts). That keeps
// invalidation partial — a team edit does not refetch a person's documents —
// and keeps authorization on the server where it belongs.
//
// Transport is graphql-ws, gated behind LIVE_ENABLED (default false): the
// server-side EDFS+NATS backbone (specs/live-updates) ships separately. With
// the flag off, wake()/focus revalidation still runs — no polling, and no
// WebSocket is ever attempted.
import * as Sentry from '@sentry/vue'
import { defineStore } from 'pinia'
import { createClient, type Client } from 'graphql-ws'
import { useAuthStore } from 'src/stores/auth'
import { traceparent } from 'src/lib/tracing'
import { createScopeRegistry } from 'src/stores/liveScopes'

interface LiveState {
  /** Bumped per kind; watchers refetch on change. Kinds are the memstore
   *  entity names in docs/live-updates.md's "What you can subscribe to"
   *  table — an open set, so an unrecognized kind is bumped exactly like a
   *  known one rather than rejected. Named distinctly from the `revision`
   *  getter below — Pinia forbids a getter and a state property sharing a
   *  name. */
  revisionByKind: Record<string, number>
  /** Last time everything was revalidated (wake() or a reconnect). Shared
   *  between the two so a reconnect that immediately follows a wake()
   *  refresh does not refresh the world twice. */
  lastRevalidate: number
}

interface LiveChange {
  service: string
  kind: string
  scope: string
  ids: string[]
}

const LIVE_SUBSCRIPTION = `
  subscription Live($scope: String!) {
    liveChanges(scope: $scope) {
      service
      kind
      scope
      ids
    }
  }
`

const MIN_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000
/**
 * How often the CLIENT pings the server, and therefore how often an idle
 * connection produces traffic the watchdog below can see.
 *
 * The watchdog used to be armed against the ROUTER's keepalive
 * (`engine.websocket_client_ping_interval: 60s` in argocd-fsgnext) — a
 * keepalive a browser cannot observe. It is a WebSocket PROTOCOL ping, and the
 * browser WebSocket API exposes no ping/pong events at all: the frame is
 * answered by the browser itself and never reaches JS. Nothing arrives at the
 * graphql-ws message layer either. So on an idle feed the watchdog saw silence
 * for its full window every time, killed a perfectly healthy socket, and the
 * reconnect's refreshAll() refetched every query on whatever page the user was
 * on. Sentry's `live.connect` spans showed the signature exactly: connects at a
 * dead-flat 151s cadence, 174 of them in a day.
 *
 * graphql-ws's own `keepAlive` is the fix: it sends a `PingMessage` in the
 * graphql-ws protocol — a real message, not a frame — the server answers
 * `PongMessage`, and that answer arrives through `on.message` like any other,
 * which is precisely what the watchdog measures.
 */
const KEEPALIVE_MS = 30_000
/**
 * Two missed keepalive round trips, plus one interval of slack for a slow
 * pong — docs/live-updates.md "Silence watchdog". Derived from KEEPALIVE_MS
 * rather than written as a number so the two can never drift apart again;
 * that drift is what made the old 150s window fire on healthy connections.
 */
const SILENCE_TIMEOUT_MS = 3 * KEEPALIVE_MS
/** Wake handlers, and the reconnect-dedup window, fire at most this often. */
const REVALIDATE_EVERY_MS = 5_000

// Connection state is not application state: a graphql-ws Client, its
// subscription handles, the watchdog timer and the DOM listener teardown are
// never serialized, diffed or devtools-tracked, so they live outside Pinia.
let client: Client | null = null
let silenceTimer: ReturnType<typeof setTimeout> | null = null
// Last moment ANY traffic (pings included) arrived on the socket — a socket
// with recent traffic has been authoritative the whole time, so wake() has
// nothing to heal (owner fix 2026-08-24).
let lastTraffic = 0
let wakeListeners: (() => void) | null = null
// Guards the TRANSPORT only, not start() as a whole — watchForReturn() has
// its own idempotency guard, so start() can safely run again once a pre-auth
// session authenticates (fsgadmin/fsgnext#125) without re-wiring listeners.
let started = false
// When reconnect() last dropped the socket ON PURPOSE (a Keycloak token
// rotation). Module-level for the same reason the socket is: connection
// bookkeeping, not application state.
let rotatedAt = 0

// The one union of every mounted page's declared scopes plus the shell's
// floor (src/stores/liveScopes.ts). Module-level for the same reason the
// socket is: it holds unsubscribe callbacks, not application state. Created
// eagerly so declarations made before start() (boot order, or a test with no
// transport at all) are held and opened the moment a transport attaches.
const scopes = createScopeRegistry()

/** Bridges the registry to graphql-ws: one `liveChanges(scope:)` operation
 *  per scope, multiplexed on the one socket, every notice bumping its kind's
 *  revision counter. */
function scopeTransport(bump: (kind: string) => void) {
  return {
    open(scope: string): () => void {
      const c = client
      if (!c) return () => {}
      return c.subscribe<{ liveChanges: LiveChange }>(
        { query: LIVE_SUBSCRIPTION, variables: { scope } },
        {
          next: (result) => {
            const change = result.data?.liveChanges
            if (change) bump(change.kind)
          },
          error: (err) => console.warn('live: subscription error', scope, err),
          // The registry owns the bookkeeping — a completion it did not ask
          // for only happens when the server ends the stream, and the next
          // reconcile() re-opens the scope if it is still wanted.
          complete: () => {},
        },
      )
    },
  }
}

/** The graphql-ws client, for src/boot/urql.ts's subscriptionExchange — one
 *  socket carries both the invalidation feed below and any GraphQL
 *  subscription operation the app issues through urql (docs/live-updates.md:
 *  subscriptions are "multiplexed on the one WebSocket"). Null when the
 *  transport is off (LIVE_ENABLED unset) or before start() has run. */
export function liveClient(): Client | null {
  return client
}

function wsUrl(): string {
  // Same endpoint as queries (owner ruling 2026-08-23: direct, no proxy),
  // http(s) scheme swapped for ws(s).
  return import.meta.env.GRAPHQL_URL.replace(/^http/, 'ws')
}

function armWatchdog(): void {
  lastTraffic = Date.now()
  if (silenceTimer) clearTimeout(silenceTimer)
  silenceTimer = setTimeout(() => {
    Sentry.addBreadcrumb({
      category: 'live',
      level: 'warning',
      message: 'silence watchdog — no traffic, dropping the connection',
      data: { timeoutMs: SILENCE_TIMEOUT_MS },
    })
    client?.terminate()
  }, SILENCE_TIMEOUT_MS)
}

/**
 * What a reconnect owes the caches — pure, so the rule is testable without a
 * socket (the transport never runs under vitest: LIVE_ENABLED is unset).
 *
 *  * `rotation` — WE dropped the link for a Keycloak token rotation and it
 *    came straight back. Healthy on both sides of a sub-second gap, so there
 *    is nothing to catch up on. A rotation whose reconnect actually took
 *    longer than the window WAS an outage and falls through to `refresh`.
 *  * `covered`  — wake() already refreshed everything; its abort is what
 *    brought the connection back in the first place.
 *  * `refresh`  — anything else. Delivery is at-most-once, so an unexplained
 *    reconnect means notices were possibly missed, and there is no way to
 *    know which.
 */
export type ReconnectVerdict = 'rotation' | 'covered' | 'refresh'

export function classifyReconnect(
  now: number,
  rotatedAt: number,
  lastRevalidate: number,
): ReconnectVerdict {
  if (rotatedAt > 0 && now - rotatedAt < REVALIDATE_EVERY_MS) return 'rotation'
  if (now - lastRevalidate < REVALIDATE_EVERY_MS) return 'covered'
  return 'refresh'
}

const RECONNECT_BREADCRUMB: Record<ReconnectVerdict, string> = {
  rotation: 'reconnected after a token rotation — nothing missed, no refresh',
  covered: 'reconnected — wake() already refreshed everything',
  refresh: 'reconnected — refreshing everything',
}

function disarmWatchdog(): void {
  if (silenceTimer) clearTimeout(silenceTimer)
  silenceTimer = null
}

export const useLiveStore = defineStore('live', {
  state: (): LiveState => ({
    revisionByKind: {},
    lastRevalidate: 0,
  }),

  getters: {
    /** `live.revision(kind)` rather than a plain property — kinds are not
     *  known ahead of time, so there is no fixed set of properties to type. */
    revision:
      (state) =>
      (kind: string): number =>
        state.revisionByKind[kind] ?? 0,
  },

  actions: {
    /** Bump a kind's revision counter. Whole-kind notices (`ids: []`) and
     *  unrecognized kinds are handled identically to a normal notice — never
     *  hard-fail on an unrecognized kind. */
    bump(kind: string) {
      this.revisionByKind[kind] = (this.revisionByKind[kind] ?? 0) + 1
    },

    /** Ensure a kind is tracked, so refreshAll() sweeps it even if it has
     *  never actually changed yet. Called by the composables that watch it
     *  (src/composables/useLive.ts) — the store only needs to refresh what
     *  something is actually watching. */
    register(kind: string) {
      if (!(kind in this.revisionByKind)) this.revisionByKind[kind] = 0
    },

    /** Bump every kind anything currently watches. Used after a reconnect —
     *  delivery is at-most-once, so a reconnect means notices were possibly
     *  missed and there is no way to know which. */
    refreshAll() {
      this.lastRevalidate = Date.now()
      for (const kind of Object.keys(this.revisionByKind)) this.revisionByKind[kind]!++
    },

    /**
     * Revalidate on the app coming back to life: visibilitychange / online,
     * throttled to once per 5s, and skipped entirely while a healthy live
     * socket proves nothing was missed. Active from day one, independent of
     * whether the WS transport is on — see the module comment.
     */
    wake() {
      const now = Date.now()
      if (now - this.lastRevalidate < REVALIDATE_EVERY_MS) return
      // A live socket with recent traffic has been authoritative the whole
      // time away — nothing was missed, nothing to refresh (owner fix
      // 2026-08-24: every window refocus used to refetch the world; with
      // the transport healthy that is pure waste). A genuinely dead socket
      // is caught either here (silence past the watchdog window) or by the
      // reconnect handler's own refreshAll().
      if (client && now - lastTraffic < SILENCE_TIMEOUT_MS) return
      // If the socket died while the tab was away, terminate()+retry is what
      // makes the NEXT change arrive; refreshAll() catches up on what was
      // already missed, whether or not the transport is even on.
      client?.terminate()
      this.refreshAll()
    },

    /**
     * Wires the wake handlers (idempotent regardless of auth state) and
     * opens the WS transport once the session is authenticated with a real
     * (non-mock) identity. A pre-auth boot (src/core/publicPaths.ts) has no
     * session to carry a bearer and must open no socket at all
     * (fsgadmin/fsgnext#125) — called from src/boot/urql.ts unconditionally
     * on every boot, and again from src/boot/keycloak.ts right after
     * authentication completes, so a session that started pre-auth still
     * gets the transport, exactly once.
     */
    start() {
      this.watchForReturn()

      if (started) return
      if (
        import.meta.env.LIVE_ENABLED !== 'true' ||
        useAuthStore().mock ||
        !useAuthStore().authenticated
      )
        return
      started = true

      client = createClient({
        url: wsUrl(),
        connectionParams: () => {
          const auth = useAuthStore()
          // Trace context on the WS connect too (owner ruling 2026-08-23:
          // every outbound request carries a traceparent). Browsers cannot
          // set WS headers, so it rides the connection_init payload beside
          // the token; a fresh span ties the connect into the session trace.
          const span = Sentry.startInactiveSpan({ name: 'live.connect', op: 'websocket.connect' })
          const params: Record<string, string> = span ? { traceparent: traceparent(span) } : {}
          span?.end()
          if (auth.token) params.Authorization = `Bearer ${auth.token}`
          return params
        },
        // Ping the server every 30s so an idle connection still produces
        // traffic the silence watchdog can observe (see KEEPALIVE_MS). The
        // server's PongMessage arrives as an ordinary message and re-arms it.
        keepAlive: KEEPALIVE_MS,
        retryAttempts: Infinity,
        shouldRetry: () => true,
        retryWait: async (retries) => {
          const delay = Math.min(MIN_BACKOFF_MS * 2 ** retries, MAX_BACKOFF_MS)
          await new Promise((resolve) => setTimeout(resolve, delay))
        },
        on: {
          connected: (_socket, _payload, wasRetry) => {
            if (wasRetry) {
              // Delivery is at-most-once, so a reconnect normally means
              // notices were possibly missed — classifyReconnect holds the
              // two cases where it demonstrably did not. The refetch this
              // decides is a visible one: it puts loading chrome on every
              // query the current page watches.
              const store = useLiveStore()
              const verdict = classifyReconnect(Date.now(), rotatedAt, store.lastRevalidate)
              rotatedAt = 0
              Sentry.addBreadcrumb({
                category: 'live',
                level: 'info',
                message: RECONNECT_BREADCRUMB[verdict],
              })
              if (verdict === 'refresh') store.refreshAll()
            } else {
              Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'connected' })
            }
            armWatchdog()
          },
          closed: (event) => {
            disarmWatchdog()
            Sentry.addBreadcrumb({
              category: 'live',
              level: 'warning',
              message: 'stream lost, reconnecting',
              data: { event: String(event) },
            })
          },
          error: (err) => {
            // The retry itself is the handling; this is dev visibility only.
            console.warn('live: connection error', err)
          },
          // Any traffic proves the link is alive — pings/pongs included.
          message: () => armWatchdog(),
        },
      })

      // Declarations made before the transport existed (the shell's floor is
      // set as soon as Query.me answers, which can beat boot's start()) are
      // held by the registry and opened here. From this point on, every
      // declare/update/release reconciles straight onto the socket.
      scopes.attach(scopeTransport((kind) => this.bump(kind)))
    },

    /**
     * Declare the scopes a mounted page's data lives under; returns the
     * handle used to update or release them.
     *
     * NOTHING calls this directly — src/composables/useLiveScopes.ts is the
     * one caller, and every routed page goes through it (ui-conventions.md
     * #104, enforced by src/router/liveScopes.invariant.test.ts). The
     * predecessor of these three actions was a public `subscribeScopes` with
     * no callers at all: the socket connected, keepalives flowed, and every
     * remote change stayed invisible until a manual reload (owner-reported
     * 2026-08-26). A declaration that a page owns and releases cannot rot
     * that way — leaving the page is what closes the subscription.
     */
    declareScopes(list: readonly string[]): number {
      return scopes.declare(list)
    },
    updateScopes(token: number, list: readonly string[]) {
      scopes.update(token, list)
    },
    releaseScopes(token: number) {
      scopes.release(token)
    },

    /**
     * The always-on floor, owned by the shell (src/layouts/MainLayout.vue):
     * the viewer's own `person:<id>`, the one scope every signed-in user has.
     * The global feed (catalogues, the four rbac_* tables, every whole-kind
     * import notice) is delivered "implicitly" — it rides along on the scopes
     * you hold — so without a floor a page that declares nothing would
     * receive nothing at all.
     *
     * A floor, not a permanent subscription: entering an impersonation swaps
     * the viewer, and the previous person's scope must CLOSE rather than
     * linger beside the new one.
     */
    setScopeFloor(list: readonly string[]) {
      scopes.setFloor(list)
    },

    /** The union of every declaration plus the floor — the set the socket
     *  mirrors. Debug/test surface; nothing in the app branches on it. */
    activeScopes(): string[] {
      return scopes.active()
    },

    /** Force the WS to reconnect — called on every Keycloak token refresh
     *  (src/boot/keycloak.ts). The router only validates at connect, so
     *  rotating the token without reconnecting means running on a stale
     *  identity. A no-op when the transport is off.
     *
     *  The scope set survives: graphql-ws re-sends the Subscribe message for
     *  every operation still open when the socket comes back (its
     *  per-subscription retry loop), so what is replayed is by construction
     *  the CURRENT set — a scope released before the drop completed and is
     *  not replayed. The reconnect's own `refreshAll()` (the `connected`
     *  handler above) covers the notices missed in between. */
    reconnect() {
      rotatedAt = Date.now()
      client?.terminate()
    },

    /** Wires visibilitychange/online/focus to wake(). Idempotent. */
    watchForReturn() {
      if (wakeListeners || typeof document === 'undefined') return
      const handler = () => {
        if (document.visibilityState === 'hidden') return
        this.wake()
      }
      // visibilitychange + online only — NOT window `focus`: focus fires on
      // every click back into the page (DevTools, another window), which
      // turned ordinary interaction into a full refetch of the world when
      // the transport is off (owner fix 2026-08-24). A tab you can SEE has
      // running timers and a live socket; the hidden→visible edge is the
      // one that needs healing.
      document.addEventListener('visibilitychange', handler)
      window.addEventListener('online', handler)
      wakeListeners = () => {
        document.removeEventListener('visibilitychange', handler)
        window.removeEventListener('online', handler)
      }
    },
  },
})
